import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import UomSelect from "@/components/UomSelect";
import BulkUploadButton from "@/components/BulkUploadButton";

const empty = { name: "", vendor: "", unit: "pcs", unit_other: "", price_per_unit: 0, notes: "" };

export default function Bop() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => { setList((await api.get("/bop")).data); };
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (b) => { setEditing(b); setForm({ ...b }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        const payload = { ...form, price_per_unit: Number(form.price_per_unit) || 0 };
        try {
            if (editing) await api.put(`/bop/${editing.bop_id}`, payload);
            else await api.post("/bop", payload);
            setOpen(false); await load();
            toast.success("Saved");
        } catch { toast.error("Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete BOP?")) return;
        await api.delete(`/bop/${id}`); await load();
    };

    return (
        <div>
            <PageHeader
                eyebrow="Master" title="Bought-Out Parts (BOP)"
                subtitle="External / finished sub-parts you buy as-is from vendors."
                actions={
                    <div className="flex gap-2">
                        <BulkUploadButton entity="bop" onComplete={load} />
                        <Button onClick={startCreate} className="rounded-none bg-[#E63946] hover:bg-[#C92836]" data-testid="new-bop-button"><Plus size={16} className="mr-2" /> Add BOP</Button>
                    </div>
                }
            />
            <div className="px-8 py-6">
                {list.length === 0 ? (
                    <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
                        <div className="tag-pill mb-4 inline-flex">Empty</div>
                        <h2 className="text-2xl font-bold tracking-tight">No BOPs yet</h2>
                        <Button onClick={startCreate} className="mt-6 rounded-none bg-[#E63946] hover:bg-[#C92836]"><Plus size={16} className="mr-2" /> Add now</Button>
                    </div>
                ) : (
                    <table className="w-full sheet-table" data-testid="bop-table">
                        <thead><tr><th>Part</th><th>Vendor</th><th>UOM</th><th>Price / Unit (₹)</th><th>Notes</th><th></th></tr></thead>
                        <tbody>
                            {list.map((b) => (
                                <tr key={b.bop_id} data-testid={`bop-row-${b.bop_id}`}>
                                    <td className="font-medium">{b.name}</td>
                                    <td>{b.vendor || "—"}</td>
                                    <td className="font-mono">{b.unit || "pcs"}</td>
                                    <td className="font-mono">{b.price_per_unit}</td>
                                    <td className="text-zinc-500 text-xs">{b.notes}</td>
                                    <td className="text-right w-32">
                                        <button onClick={() => startEdit(b)} className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider">Edit</button>
                                        <button onClick={() => remove(b.bop_id)} className="ml-3 text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} BOP</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Name *"><Input data-testid="bop-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Vendor"><Input data-testid="bop-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></Field>
                        <Field label="Unit">
                            <UomSelect
                                unit={form.unit || ""}
                                unitOther={form.unit_other || ""}
                                onChange={(patch) => setForm({ ...form, ...patch })}
                                testIdPrefix="bop-master-uom"
                            />
                        </Field>
                        <Field label="Price / Unit (₹)"><Input type="number" data-testid="bop-price-input" value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })} /></Field>
                        <div className="col-span-2">
                            <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#E63946] hover:bg-[#C92836]" onClick={save} data-testid="save-bop-button">{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>{children}</div>;
}
